import * as React from 'react';

import { cn } from '@/lib/utils';

import { SidebarPanel } from './sidebar-panel';
import { useSidebar } from './sidebar-context';

export { SidebarContentReveal } from './content-reveal';
export { SidebarPanel } from './sidebar-panel';

/**
 * The app sidebar.
 *
 * Below `lg` it is a drawer that always shows full labels — the desktop
 * collapse preference is deliberately ignored there, so a phone can never
 * lose its navigation. At `lg`+ the expanded state is a regular static
 * column; collapsing hides the sidebar completely (the column animates to
 * zero width). The collapsed state's left-edge reveal — peel or slide-in —
 * lives in `SidebarContentReveal`, which wraps the content column.
 */
export function Sidebar({ mobileOpen, onMobileClose }: { mobileOpen: boolean; onMobileClose: () => void }) {
  const { collapsed } = useSidebar();

  // The width transition only attaches after mount so the persisted collapsed
  // width never animates into place on first paint.
  const [mounted, setMounted] = React.useState(false);
  React.useEffect(() => setMounted(true), []);

  return (
    <>
      {/* Mobile drawer: always full labels, never collapsed, never peeled. */}
      {mobileOpen ? (
        <div
          className="fixed inset-0 z-30 animate-fade-in bg-black/40 lg:hidden"
          onClick={onMobileClose}
          aria-hidden
        />
      ) : null}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-40 w-60 border-r border-line bg-surface shadow-pop transition-transform duration-300 lg:hidden',
          mobileOpen ? 'translate-x-0' : '-translate-x-full',
        )}
        aria-label="Main navigation"
      >
        <SidebarPanel footer="none" />
      </aside>

      {/* Desktop column. Collapsed = gone entirely; the content column's
          SidebarContentReveal owns the left-edge reveal from then on. */}
      <aside
        className={cn(
          'relative z-40 hidden shrink-0 overflow-hidden bg-surface lg:block',
          collapsed ? 'w-0' : 'w-60 border-r border-line',
          mounted && 'transition-[width] duration-300 ease-out',
        )}
        aria-label="Main navigation"
        aria-hidden={collapsed}
      >
        {/* Fixed inner width so the panel slides out instead of squashing. */}
        <div className="h-full w-60">
          <SidebarPanel footer="collapse" />
        </div>
      </aside>
    </>
  );
}
